/**
 * Diagnostic logging for the CSV column-matching engine (CRM-1174 + CRM-1175).
 *
 * Every matching run can print a structured, greppable trace to the console so a
 * failed or surprising mapping can be analysed without a debugger. Prefix is
 * always `[csv-matching]`; each entry names the stage that produced it.
 *
 * Silent in production: logging is on only on a dev build (localhost) or when a
 * user opts in with `?csvDebug` in the URL — the same escape hatch pattern the
 * AiChatBot uses (`?aiDebug`). A normal production import never logs.
 */

import type { MatchCost } from './cost'
import type { MatchConfidence, MatchResult } from './types'

const PREFIX = '[csv-matching]'

const LOGGING_ENABLED =
  import.meta.env.DEV ||
  (typeof location !== 'undefined' &&
    new URLSearchParams(location.search).has('csvDebug'))

export function logAiRequest(info: {
  model: string
  fileLines: number
  candidateCount: number
}): void {
  if (!LOGGING_ENABLED) {
    return
  }
  console.info(`${PREFIX}[ai] → request`, info)
}

export function logAiRawResponse(text: string): void {
  if (!LOGGING_ENABLED) {
    return
  }
  console.debug(`${PREFIX}[ai] ← raw response`, text)
}

export function logAiResult(info: {
  delimiter: string
  headerRowIndex: number
  matched: number
  total: number
  elapsedMs: number
}): void {
  if (!LOGGING_ENABLED) {
    return
  }
  console.info(`${PREFIX}[ai] ← parsed`, info)
}

export function logAiCost(cost: MatchCost): void {
  if (!LOGGING_ENABLED) {
    return
  }
  console.info(
    `${PREFIX}[ai] cost — model=${cost.model} ` +
      `credits(in/out)=${cost.inputCredits}/${cost.outputCredits} ` +
      `~tokens(in/out)=${cost.inputTokens}/${cost.outputTokens} ` +
      `USD=$${cost.usd.toFixed(6)}`,
  )
}

export function logAiFailure(error: unknown): void {
  if (!LOGGING_ENABLED) {
    return
  }
  console.warn(`${PREFIX}[ai] failed, falling back to fuzzy:`, error)
}

/**
 * One line per header-row detection, replacing the old per-row "running" spam.
 * Reports how many of the (capped) leading rows were actually scanned, which row
 * won, its match ratio, and whether the confident-ratio short-circuit fired.
 */
export function logHeaderScan(info: {
  scanned: number
  scanLimit: number
  chosenRow: number
  matched: number
  columns: number
  shortCircuited: boolean
}): void {
  if (!LOGGING_ENABLED) {
    return
  }
  const stop = info.shortCircuited
    ? `short-circuit after ${info.scanned}`
    : `scanned ${info.scanned}/${info.scanLimit}`
  console.info(
    `${PREFIX}[fuzzy] header-scan — ${stop}, ` +
      `chose row ${info.chosenRow} (${info.matched}/${info.columns} cols matched)`,
  )
}

/**
 * Final per-column decision, printed once mappings are applied. Uses the real
 * parsed header row so each line reads `header → fieldKey (confidence)`.
 * Unmatched columns are shown explicitly as rejections.
 */
export function logMatchSummary(
  source: 'ai' | 'fuzzy' | 'merged',
  headers: string[],
  matches: MatchResult[],
): void {
  if (!LOGGING_ENABLED) {
    return
  }
  const byIndex = new Map<number, MatchResult>(
    matches.map((m) => [m.rawIndex, m]),
  )
  const rows = headers.map((header, rawIndex) => {
    const match = byIndex.get(rawIndex)
    const fieldKey = match?.fieldKey ?? null
    const confidence: MatchConfidence | '—' = match?.fieldKey
      ? match.confidence
      : '—'
    return {
      col: rawIndex,
      header,
      fieldKey: fieldKey ?? '(rejected)',
      confidence,
      ...(match?.score !== undefined ? { levenshtein: match.score } : {}),
    }
  })

  const matched = rows.filter((r) => r.fieldKey !== '(rejected)').length
  console.info(
    `${PREFIX}[${source}] summary — ${matched}/${headers.length} columns matched`,
  )
  console.table(rows)
}
